/**
 * This code was AUTOGENERATED using the codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type IAccountMeta,
  type IInstruction,
  type IInstructionWithAccounts,
  type IInstructionWithData,
  type ReadonlyAccount,
} from '@solana/kit';
import {
  getStakeAccountAssertionsDecoder,
  getStakeAccountAssertionsEncoder,
  type StakeAccountAssertions,
  type StakeAccountAssertionsArgs,
} from '../../hooked';
import { LIGHTHOUSE_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';
import {
  LogLevel,
  getLogLevelDecoder,
  getLogLevelEncoder,
  type LogLevelArgs,
} from '../types';

export const ASSERT_STAKE_ACCOUNT_MULTI_DISCRIMINATOR = 12;

export function getAssertStakeAccountMultiDiscriminatorBytes() {
  return getU8Encoder().encode(ASSERT_STAKE_ACCOUNT_MULTI_DISCRIMINATOR);
}

export type AssertStakeAccountMultiInstruction<
  TProgram extends string = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
  TAccountTargetAccount extends string | IAccountMeta<string> = string,
  TRemainingAccounts extends readonly IAccountMeta<string>[] = [],
> = IInstruction<TProgram> &
  IInstructionWithData<Uint8Array> &
  IInstructionWithAccounts<
    [
      TAccountTargetAccount extends string
        ? ReadonlyAccount<TAccountTargetAccount>
        : TAccountTargetAccount,
      ...TRemainingAccounts,
    ]
  >;

export type AssertStakeAccountMultiInstructionData = {
  discriminator: number;
  logLevel: LogLevel;
  assertions: StakeAccountAssertions;
};

export type AssertStakeAccountMultiInstructionDataArgs = {
  logLevel?: LogLevelArgs;
  assertions: StakeAccountAssertionsArgs;
};

export function getAssertStakeAccountMultiInstructionDataEncoder(): Encoder<AssertStakeAccountMultiInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', getU8Encoder()],
      ['logLevel', getLogLevelEncoder()],
      ['assertions', getStakeAccountAssertionsEncoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: ASSERT_STAKE_ACCOUNT_MULTI_DISCRIMINATOR,
      logLevel: value.logLevel ?? LogLevel.Silent,
    })
  );
}

export function getAssertStakeAccountMultiInstructionDataDecoder(): Decoder<AssertStakeAccountMultiInstructionData> {
  return getStructDecoder([
    ['discriminator', getU8Decoder()],
    ['logLevel', getLogLevelDecoder()],
    ['assertions', getStakeAccountAssertionsDecoder()],
  ]);
}

export function getAssertStakeAccountMultiInstructionDataCodec(): Codec<
  AssertStakeAccountMultiInstructionDataArgs,
  AssertStakeAccountMultiInstructionData
> {
  return combineCodec(
    getAssertStakeAccountMultiInstructionDataEncoder(),
    getAssertStakeAccountMultiInstructionDataDecoder()
  );
}

export type AssertStakeAccountMultiInput<
  TAccountTargetAccount extends string = string,
> = {
  /** Target account to be asserted */
  targetAccount: Address<TAccountTargetAccount>;
  logLevel?: AssertStakeAccountMultiInstructionDataArgs['logLevel'];
  assertions: AssertStakeAccountMultiInstructionDataArgs['assertions'];
};

export function getAssertStakeAccountMultiInstruction<
  TAccountTargetAccount extends string,
  TProgramAddress extends Address = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
>(
  input: AssertStakeAccountMultiInput<TAccountTargetAccount>,
  config?: { programAddress?: TProgramAddress }
): AssertStakeAccountMultiInstruction<TProgramAddress, TAccountTargetAccount> {
  // Program address.
  const programAddress = config?.programAddress ?? LIGHTHOUSE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    targetAccount: { value: input.targetAccount ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  const instruction = {
    accounts: [getAccountMeta(accounts.targetAccount)],
    programAddress,
    data: getAssertStakeAccountMultiInstructionDataEncoder().encode(
      args as AssertStakeAccountMultiInstructionDataArgs
    ),
  } as AssertStakeAccountMultiInstruction<
    TProgramAddress,
    TAccountTargetAccount
  >;

  return instruction;
}

export type ParsedAssertStakeAccountMultiInstruction<
  TProgram extends string = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly IAccountMeta[] = readonly IAccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Target account to be asserted */
    targetAccount: TAccountMetas[0];
  };
  data: AssertStakeAccountMultiInstructionData;
};

export function parseAssertStakeAccountMultiInstruction<
  TProgram extends string,
  TAccountMetas extends readonly IAccountMeta[],
>(
  instruction: IInstruction<TProgram> &
    IInstructionWithAccounts<TAccountMetas> &
    IInstructionWithData<Uint8Array>
): ParsedAssertStakeAccountMultiInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 1) {
    // TODO: Coded error.
    throw new Error('Not enough accounts');
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = instruction.accounts![accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      targetAccount: getNextAccount(),
    },
    data: getAssertStakeAccountMultiInstructionDataDecoder().decode(
      instruction.data
    ),
  };
}
